import { useState, useRef, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ContentItem {
  id: string
  title: string
  cefr_level: 'A2' | 'B1' | 'B2' | 'C1'
  vstep_parts: string[]
  topic_tags: string[]
  status: 'pending_audio' | 'published' | 'archived'
  voice_id: string | null
  accent: 'american' | 'british' | null
  url: string | null
  transcript: string | null
  duration_seconds: number | null
  created_at: string
  exam_type?: 'vstep' | 'ielts_academic' | 'ielts_general'
}

interface ListeningContentResponse {
  items: ContentItem[]
  total: number
  page: number
  limit: number
}

// ─── Status badge helper ──────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContentItem['status'] }) {
  const styles: Record<ContentItem['status'], React.CSSProperties> = {
    pending_audio: { background: '#fff3cd', color: '#856404', border: '1px solid #ffc107' },
    published:     { background: '#d1e7dd', color: '#0a3622', border: '1px solid #198754' },
    archived:      { background: '#e2e3e5', color: '#41464b', border: '1px solid #adb5bd' },
  }
  const labels: Record<ContentItem['status'], string> = {
    pending_audio: 'Pending Audio',
    published:     'Published',
    archived:      'Archived',
  }
  return (
    <span style={{ ...styles[status], padding: '2px 8px', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {labels[status]}
    </span>
  )
}

// ─── Inline audio player ──────────────────────────────────────────────────────

function InlineAudioPlayer({ url, itemId, playingId, setPlayingId }: {
  url: string
  itemId: string
  playingId: string | null
  setPlayingId: (id: string | null) => void
}) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const isPlaying = playingId === itemId

  // Pause this audio when another item starts playing
  useEffect(() => {
    if (!isPlaying && audioRef.current) {
      audioRef.current.pause()
    }
  }, [isPlaying])

  function toggle() {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      setPlayingId(null)
    } else {
      setPlayingId(itemId)
      audioRef.current.play().catch(() => {
        // URL not playable (e.g. dev-local placeholder) — just mark as not playing
        setPlayingId(null)
      })
    }
  }

  return (
    <>
      <audio
        ref={audioRef}
        src={url}
        onEnded={() => setPlayingId(null)}
        style={{ display: 'none' }}
      />
      <button
        onClick={toggle}
        title={isPlaying ? 'Dừng' : 'Phát audio'}
        style={{
          background: isPlaying ? '#004be2' : '#eef0ff',
          color: isPlaying ? 'white' : '#004be2',
          border: 'none', borderRadius: '50%', width: '2rem', height: '2rem',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>
          {isPlaying ? 'pause' : 'play_arrow'}
        </span>
      </button>
    </>
  )
}

// ─── Upload Audio Modal ───────────────────────────────────────────────────────

function UploadAudioModal({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null
    setFileError(null)
    setUploadError(null)

    if (!selected) {
      setFile(null)
      return
    }

    if (selected.type !== 'audio/mpeg') {
      setFile(null)
      setFileError('Chỉ chấp nhận file .mp3 (audio/mpeg). Vui lòng chọn lại.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setFile(selected)
  }

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('audio', file)

      const result = await api.postFormData<{ id: string; url: string; status: string }>(
        `/admin/listening-content/${item.id}/upload-audio`,
        formData,
      )

      // Update the item in all matching React Query caches
      queryClient.setQueriesData<ListeningContentResponse>(
        { queryKey: ['admin-listening-content'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            items: old.items.map((i) =>
              i.id === item.id
                ? { ...i, status: result.status as ContentItem['status'], url: result.url }
                : i,
            ),
          }
        },
      )

      onClose()
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload thất bại. Vui lòng thử lại.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', maxWidth: '36rem', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem' }}>Upload Audio — {item.title}</h3>

        {/* Transcript reference */}
        {item.transcript && (
          <div>
            <p style={{ margin: '0 0 0.375rem', fontSize: '0.8125rem', fontWeight: 600, color: '#535b71' }}>Transcript (để đối chiếu)</p>
            <div style={{
              maxHeight: '10rem', overflowY: 'auto', background: '#f8f9fa',
              border: '1px solid #dee2e6', borderRadius: '0.5rem',
              padding: '0.75rem', fontSize: '0.8125rem', lineHeight: 1.6, color: '#212529',
              whiteSpace: 'pre-wrap',
            }}>
              {item.transcript}
            </div>
          </div>
        )}

        {/* File input */}
        <div>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#535b71', marginBottom: '0.375rem' }}>
            Chọn file .mp3
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,audio/mpeg"
            onChange={handleFileChange}
            disabled={uploading}
            style={{ display: 'block', width: '100%', fontSize: '0.875rem' }}
          />
          {fileError && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: '#b41340' }}>{fileError}</p>
          )}
          {file && !fileError && (
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.8125rem', color: '#0a3622' }}>
              ✓ {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
        </div>

        {/* Upload error */}
        {uploadError && (
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#b41340', background: '#fff0f3', border: '1px solid #f5c2cb', borderRadius: '0.5rem', padding: '0.625rem 0.875rem' }}>
            {uploadError}
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={uploading}>
            Hủy
          </button>
          <button
            className="btn-primary"
            onClick={handleUpload}
            disabled={!file || !!fileError || uploading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {uploading && (
              <span style={{
                width: '0.875rem', height: '0.875rem', border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: 'white', borderRadius: '50%',
                display: 'inline-block', animation: 'spin 0.7s linear infinite',
              }} />
            )}
            {uploading ? 'Đang upload…' : 'Upload'}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Edit Metadata Modal ──────────────────────────────────────────────────────

const VSTEP_PARTS = ['part1', 'part2', 'part3'] as const

function EditMetadataModal({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(item.title)
  const [voiceId, setVoiceId] = useState(item.voice_id ?? '')
  const [accent, setAccent] = useState<string>(item.accent ?? '')
  const [topicTags, setTopicTags] = useState((item.topic_tags ?? []).join(', '))
  const [cefrLevel, setCefrLevel] = useState<string>(item.cefr_level ?? '')
  const [vstepParts, setVstepParts] = useState<string[]>(item.vstep_parts ?? [])
  const [examType, setExamType] = useState<string>(item.exam_type ?? 'vstep')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggleVstepPart(part: string) {
    setVstepParts((prev) =>
      prev.includes(part) ? prev.filter((p) => p !== part) : [...prev, part],
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    const body: Record<string, unknown> = {
      title,
      voice_id: voiceId || null,
      accent: accent || null,
      topic_tags: topicTags.split(',').map((t) => t.trim()).filter(Boolean),
      cefr_level: cefrLevel || null,
      vstep_parts: vstepParts,
      exam_type: examType,
    }

    try {
      const updated = await api.patch<ContentItem>(`/admin/listening-content/${item.id}`, body)

      queryClient.setQueriesData<ListeningContentResponse>(
        { queryKey: ['admin-listening-content'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            items: old.items.map((i) => (i.id === item.id ? { ...i, ...updated } : i)),
          }
        },
      )

      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Cập nhật thất bại. Vui lòng thử lại.')
    } finally {
      setSubmitting(false)
    }
  }

  const fieldLabel: React.CSSProperties = { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#535b71', marginBottom: '0.25rem' }
  const fieldInput: React.CSSProperties = { width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #dee2e6', borderRadius: '0.5rem', fontSize: '0.875rem', boxSizing: 'border-box' }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', maxWidth: '38rem', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem' }}>Edit Metadata — {item.title}</h3>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {/* Title */}
          <div>
            <label style={fieldLabel}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              style={fieldInput}
            />
          </div>

          {/* Voice ID */}
          <div>
            <label style={fieldLabel}>Voice ID</label>
            <input
              type="text"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder="e.g. af_heart, bm_george"
              disabled={submitting}
              style={fieldInput}
            />
          </div>

          {/* Accent */}
          <div>
            <label style={fieldLabel}>Accent</label>
            <select value={accent} onChange={(e) => setAccent(e.target.value)} disabled={submitting} style={fieldInput}>
              <option value="">— Không chọn —</option>
              <option value="american">American</option>
              <option value="british">British</option>
            </select>
          </div>

          {/* Exam type — chosen first so the level scale matches the exam */}
          <div>
            <label style={fieldLabel}>Kỳ thi</label>
            <select value={examType} onChange={(e) => setExamType(e.target.value)} disabled={submitting} style={fieldInput}>
              <option value="vstep">VSTEP</option>
              <option value="ielts_academic">IELTS Academic</option>
              <option value="ielts_general">IELTS General</option>
            </select>
          </div>

          {/* Level (CEFR bucket — engine filters on this) */}
          <div>
            <label style={fieldLabel}>Cấp độ</label>
            <select value={cefrLevel} onChange={(e) => setCefrLevel(e.target.value)} disabled={submitting} style={fieldInput}>
              <option value="">— Không chọn —</option>
              <option value="A2">A2</option>
              <option value="B1">B1</option>
              <option value="B2">B2</option>
              <option value="C1">C1</option>
            </select>
          </div>

          {/* Topic Tags */}
          <div>
            <label style={fieldLabel}>Topic Tags (phân cách bằng dấu phẩy)</label>
            <input
              type="text"
              value={topicTags}
              onChange={(e) => setTopicTags(e.target.value)}
              placeholder="e.g. travel, business, education"
              disabled={submitting}
              style={fieldInput}
            />
          </div>

          {/* VSTEP Parts */}
          <div>
            <label style={fieldLabel}>VSTEP Parts</label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {VSTEP_PARTS.map((part) => (
                <label key={part} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={vstepParts.includes(part)}
                    onChange={() => toggleVstepPart(part)}
                    disabled={submitting}
                  />
                  {part.replace('part', 'Part ')}
                </label>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#b41340', background: '#fff0f3', border: '1px solid #f5c2cb', borderRadius: '0.5rem', padding: '0.625rem 0.875rem' }}>
              {error}
            </p>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Hủy
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {submitting && (
                <span style={{
                  width: '0.875rem', height: '0.875rem', border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: 'white', borderRadius: '50%',
                  display: 'inline-block', animation: 'spin 0.7s linear infinite',
                }} />
              )}
              {submitting ? 'Đang lưu…' : 'Lưu thay đổi'}
            </button>
          </div>
        </form>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ListeningContentTab() {
  // Filter state
  const [status, setStatus] = useState<string>('')
  const [cefrLevel, setCefrLevel] = useState<string>('')
  const [vstepPart, setVstepPart] = useState<string>('')

  // Pagination
  const [page, setPage] = useState(1)

  // Modal state — will be wired up in tasks 8.2–8.5
  const [uploadModal, setUploadModal] = useState<ContentItem | null>(null)
  const [editModal, setEditModal] = useState<ContentItem | null>(null)

  // Audio player state
  const [playingId, setPlayingId] = useState<string | null>(null)

  // Build query string
  const params = new URLSearchParams()
  if (status)    params.set('status', status)
  if (cefrLevel) params.set('cefr_level', cefrLevel)
  if (vstepPart) params.set('vstep_part', vstepPart)
  params.set('page', String(page))

  const { data, isLoading, isError } = useQuery<ListeningContentResponse>({
    queryKey: ['admin-listening-content', status, cefrLevel, vstepPart, page],
    queryFn: () => api.get<ListeningContentResponse>(`/admin/listening-content?${params.toString()}`),
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / (data?.limit ?? 20)))

  function handleFilterChange(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLSelectElement>) => {
      setter(e.target.value)
      setPage(1)
    }
  }

  return (
    <div className="admin-tab-panel">
      {/* Filters */}
      <div className="admin-toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <select value={status} onChange={handleFilterChange(setStatus)}>
          <option value="">Tất cả trạng thái</option>
          <option value="pending_audio">Pending Audio</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>

        <select value={cefrLevel} onChange={handleFilterChange(setCefrLevel)}>
          <option value="">Tất cả cấp độ</option>
          <option value="A2">A2</option>
          <option value="B1">B1</option>
          <option value="B2">B2</option>
          <option value="C1">C1</option>
        </select>

        <select value={vstepPart} onChange={handleFilterChange(setVstepPart)}>
          <option value="">Tất cả Part</option>
          <option value="part1">Part 1</option>
          <option value="part2">Part 2</option>
          <option value="part3">Part 3</option>
        </select>

        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: '#535b71', alignSelf: 'center' }}>
          {total} bản ghi
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="loading">Đang tải...</div>
      ) : isError ? (
        <div style={{ color: '#b41340', padding: '1rem' }}>Không thể tải dữ liệu. Vui lòng thử lại.</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#535b71' }}>Không có nội dung nào.</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Cấp độ</th>
              <th>VSTEP Part</th>
              <th>Status</th>
              <th>Voice ID</th>
              <th>Accent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className="table-title" style={{ maxWidth: '16rem' }}>
                  <span title={item.title}>
                    {item.title.length > 60 ? `${item.title.slice(0, 60)}…` : item.title}
                  </span>
                </td>

                <td>{item.cefr_level ?? '—'}</td>

                <td>
                  {item.vstep_parts?.length
                    ? item.vstep_parts.map((p) => p.replace('part', 'Part ')).join(', ')
                    : '—'}
                </td>

                <td><StatusBadge status={item.status} /></td>

                <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                  {item.voice_id ?? '—'}
                </td>

                <td>{item.accent ?? '—'}</td>

                <td>
                  <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                    {/* Upload Audio — only for pending_audio items */}
                    {item.status === 'pending_audio' && (
                      <button
                        className="btn-primary"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}
                        onClick={() => setUploadModal(item)}
                      >
                        Upload Audio
                      </button>
                    )}

                    {/* Play button — only for published items with a URL */}
                    {item.status === 'published' && item.url && (
                      <InlineAudioPlayer
                        url={item.url}
                        itemId={item.id}
                        playingId={playingId}
                        setPlayingId={setPlayingId}
                      />
                    )}

                    {/* Edit — always visible */}
                    <button
                      className="btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}
                      onClick={() => setEditModal(item)}
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
        <button
          className="btn-secondary"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          ← Previous
        </button>
        <span style={{ fontSize: '0.875rem', color: '#535b71' }}>
          Trang {page} / {totalPages}
        </span>
        <button
          className="btn-secondary"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next →
        </button>
      </div>

      {/* Upload Audio modal */}
      {uploadModal && (
        <UploadAudioModal
          item={uploadModal}
          onClose={() => setUploadModal(null)}
        />
      )}

      {/* Edit Metadata modal */}
      {editModal && (
        <EditMetadataModal
          item={editModal}
          onClose={() => setEditModal(null)}
        />
      )}
    </div>
  )
}
